// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Field Pickup
// Mobile-first crew interface for capturing pickups with photos
// and GPS as they work along a street / through a zone
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  SimpleGrid, Textarea, TextInput, ActionIcon, Divider,
} from '../../components/gab/GabComponents';
import {
  IconCamera, IconMapPin, IconTrash, IconPlus, IconCheck,
  IconRefresh, IconPhoto, IconCurrentLocation,
} from '@tabler/icons-react';
import { searchAddress, type AddressResult } from '../../utils/perimeterOverpass';
import { notifications } from '../../lib/notifications';
import { fmtDateTime } from '../../lib/date-format';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPES, DEBRIS_TYPE_OPTIONS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import QRCode from 'qrcode';
import { TABLES, FIELDS, updateRecord } from '../../lib/api-switch';

// DEBRIS_TYPE_OPTIONS used instead of local select

interface PickupStop {
  id: string;
  address: string;
  lat: string;
  lng: string;
  debrisType: string;
  // Per-stop measurement (adapts to debris type)
  estimatedCY: string;   // for volumetric types
  unitCount: string;
  unitSubcategory: string;
  diameterRange: string;
  weight: string;
  weightUnit: string;
  notes: string;
  beforePhotoData: string | null;  // base64 data URL for preview
  afterPhotoData: string | null;
  beforePhotoName: string;
  afterPhotoName: string;
  timestamp: string;
}

function generateId() {
  return `stop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

function getRawBase64(dataUrl: string): string {
  return dataUrl.split(',')[1] ?? '';
}

async function uploadPhotoToRecord(
  recordId: number,
  fieldKey: string,
  _fileName: string,
  base64Raw: string,
  token?: string,
) {
  await updateRecord(TABLES.debrisPhotos, recordId, [[fieldKey, base64Raw]], token);
}

export default function FieldPickup() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const token = useAuthStore.getState().token ?? undefined;

  const { zones, crews, trucks, loadZones, loadCrews, loadTrucks } = useDebrisStore();
  const { createPhoto, loadPhotos, createLoadTicket } = useDebrisOpsStore();

  const [selectedZone, setSelectedZone] = useState<string | null>(null);

  // Address autocomplete
  const [addrResults, setAddrResults] = useState<AddressResult[]>([]);
  const [showAddrResults, setShowAddrResults] = useState(false);
  const addrTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleAddrSearch = (val: string) => {
    if (currentStop) setCurrentStop(prev => prev ? { ...prev, address: val } : prev);
    if (addrTimeoutRef.current) clearTimeout(addrTimeoutRef.current);
    if (val.trim().length < 3) { setAddrResults([]); setShowAddrResults(false); return; }
    addrTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchAddress(val.trim());
        setAddrResults(results);
        setShowAddrResults(true);
      } catch { setAddrResults([]); }
    }, 400);
  };
  const [selectedCrew, setSelectedCrew] = useState<string | null>(null);
  const [stops, setStops] = useState<PickupStop[]>([]);
  const [saving, setSaving] = useState(false);
  // QR code shown after ticket generation
  const [generatedTicketQR, setGeneratedTicketQR] = useState<string>('');
  const [generatedTicketNum, setGeneratedTicketNum] = useState<string>('');
  const [gettingLocation, setGettingLocation] = useState(false);

  // Current stop being edited
  const [currentStop, setCurrentStop] = useState<PickupStop | null>(null);

  useEffect(() => { loadZones(); loadCrews(); loadTrucks(); }, [loadZones, loadCrews, loadTrucks]);

  // Warn before leaving with unsaved stops (#16)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (stops.length > 0 || currentStop) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [stops, currentStop]);

  const iZones = incidentId ? zones : [];
  const iCrews = incidentId ? crews : [];

  const zoneSelect = useMemo(() => iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `Zone #${z.id}` })), [iZones]);
  const crewSelect = useMemo(() => iCrews.map(c => ({ value: String(c.id), label: c.crewName || `Crew #${c.id}` })), [iCrews]);

  const getGPS = useCallback(() => {
    if (!navigator.geolocation) {
      notifications.show({ message: 'GPS not available on this device', color: '#C68700' });
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (currentStop) {
          setCurrentStop(prev => prev ? {
            ...prev,
            lat: pos.coords.latitude.toFixed(6),
            lng: pos.coords.longitude.toFixed(6),
          } : prev);
        }
        setGettingLocation(false);
      },
      (err) => {
        notifications.show({ message: `GPS error: ${err.message}`, color: 'var(--color-error)' });
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [currentStop]);

  const startNewStop = () => {
    const stop: PickupStop = {
      id: generateId(),
      address: '',
      lat: '',
      lng: '',
      debrisType: '',
      estimatedCY: '',
      unitCount: '',
      unitSubcategory: '',
      diameterRange: '',
      weight: '',
      weightUnit: 'lbs',
      notes: '',
      beforePhotoData: null,
      afterPhotoData: null,
      beforePhotoName: '',
      afterPhotoName: '',
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
    };
    setCurrentStop(stop);
    // Auto-get GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCurrentStop(prev => prev ? {
            ...prev,
            lat: pos.coords.latitude.toFixed(6),
            lng: pos.coords.longitude.toFixed(6),
          } : prev);
        },
        () => { /* GPS failed, user can enter manually */ },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }
  };

  const handlePhoto = async (type: 'before' | 'after', file: File | null) => {
    if (!file || !currentStop) return;
    const dataUrl = await fileToBase64(file);
    if (type === 'before') {
      setCurrentStop(prev => prev ? { ...prev, beforePhotoData: dataUrl, beforePhotoName: file.name } : prev);
    } else {
      setCurrentStop(prev => prev ? { ...prev, afterPhotoData: dataUrl, afterPhotoName: file.name } : prev);
    }
  };

  const saveStop = () => {
    if (!currentStop) return;
    if (!currentStop.address.trim() && !currentStop.lat) {
      notifications.show({ message: 'Enter an address or get GPS location', color: '#C68700' });
      return;
    }
    setStops(prev => [...prev, currentStop]);
    setCurrentStop(null);
  };

  const removeStop = (id: string) => {
    setStops(prev => prev.filter(s => s.id !== id));
  };

  const saveAllToServer = async () => {
    if (stops.length === 0) {
      notifications.show({ message: 'No pickup stops to save', color: '#C68700' });
      return;
    }
    setSaving(true);
    let saved = 0;
    let failed = 0;

    for (const stop of stops) {
      try {
        // Create "before" photo record
        if (stop.beforePhotoData) {
          const record = await createPhoto({
            parentId: selectedZone ?? '',
            parentType: 'field_pickup',
            incidentId,
            photoUrl: '',
            caption: `BEFORE — ${stop.address || `${stop.lat}, ${stop.lng}`}${stop.debrisType ? ` (${DEBRIS_TYPE_LABELS[stop.debrisType as DebrisType] ?? stop.debrisType})` : ''}`,
            geoLat: stop.lat,
            geoLng: stop.lng,
            takenAt: stop.timestamp,
            uploadedBy: user?.email ?? '',
          });
          // Upload the actual file
          if (record?.id) {
            await uploadPhotoToRecord(
              record.id,
              FIELDS.debrisPhotos.photoUrl,
              stop.beforePhotoName || 'before.jpg',
              getRawBase64(stop.beforePhotoData),
              token,
            );
          }
        }

        // Create "after" photo record
        if (stop.afterPhotoData) {
          const record = await createPhoto({
            parentId: selectedZone ?? '',
            parentType: 'field_pickup',
            incidentId,
            photoUrl: '',
            caption: `AFTER — ${stop.address || `${stop.lat}, ${stop.lng}`}${stop.debrisType ? ` (${DEBRIS_TYPE_LABELS[stop.debrisType as DebrisType] ?? stop.debrisType})` : ''}`,
            geoLat: stop.lat,
            geoLng: stop.lng,
            takenAt: stop.timestamp,
            uploadedBy: user?.email ?? '',
          });
          if (record?.id) {
            await uploadPhotoToRecord(
              record.id,
              FIELDS.debrisPhotos.photoUrl,
              stop.afterPhotoName || 'after.jpg',
              getRawBase64(stop.afterPhotoData),
              token,
            );
          }
        }

        // If no photos, still create a record to log the stop
        if (!stop.beforePhotoData && !stop.afterPhotoData) {
          await createPhoto({
            parentId: selectedZone ?? '',
            parentType: 'field_pickup',
            incidentId,
            photoUrl: '',
            caption: `Pickup — ${stop.address || `${stop.lat}, ${stop.lng}`}${stop.debrisType ? ` (${DEBRIS_TYPE_LABELS[stop.debrisType as DebrisType] ?? stop.debrisType})` : ''}${stop.notes ? ` — ${stop.notes}` : ''}`,
            geoLat: stop.lat,
            geoLng: stop.lng,
            takenAt: stop.timestamp,
            uploadedBy: user?.email ?? '',
          });
        }

        saved++;
      } catch {
        failed++;
      }
    }

    await loadPhotos();
    notifications.show({
      message: `Saved ${saved} pickup stops${failed > 0 ? `, ${failed} failed` : ''}`,
      color: failed > 0 ? '#C68700' : 'var(--color-success)',
    });
    if (failed === 0) setStops([]);
    setSaving(false);
  };

  // ── Complete Pickup & Generate Draft Ticket ─────────────────
  // Saves all stops to the server AND creates a draft load ticket
  // pre-filled with the crew's field data. The ticket can then be
  // completed by a monitor at the disposal site.
  const completePickupAndGenerateTicket = async () => {
    if (stops.length === 0) {
      notifications.show({ message: 'No stops to generate a ticket from', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      // Save photos first
      await saveAllToServer();

      // Determine dominant debris type from the stops
      const typeCounts: Record<string, number> = {};
      stops.forEach(s => {
        if (s.debrisType) typeCounts[s.debrisType] = (typeCounts[s.debrisType] || 0) + 1;
      });
      const dominantType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'vegetative';
      const cfg = DEBRIS_TYPES[dominantType as DebrisType];

      // Compute centroid of all stops (lat/lng are stored as strings)
      const validStops = stops.filter(s => s.lat && s.lng);
      const centroidLat = validStops.length > 0
        ? (validStops.reduce((sum, s) => sum + parseFloat(s.lat), 0) / validStops.length).toFixed(6)
        : '';
      const centroidLng = validStops.length > 0
        ? (validStops.reduce((sum, s) => sum + parseFloat(s.lng), 0) / validStops.length).toFixed(6)
        : '';

      // Build address from first stop
      const firstAddr = stops.find(s => s.address)?.address || '';

      // Find the truck from the selected crew
      const crew = iCrews.find(c => String(c.id) === selectedCrew);

      // Generate ticket number
      const ticketNum = `DT-${Date.now().toString(36).toUpperCase()}`;

      // Serialize stops for the ticket record
      const stopsJson = JSON.stringify(stops.map(s => ({
        id: s.id, address: s.address, lat: s.lat, lng: s.lng,
        debrisType: s.debrisType, estimatedCY: s.estimatedCY,
        unitCount: s.unitCount, weight: s.weight, weightUnit: s.weightUnit,
        notes: s.notes, timestamp: s.timestamp,
      })));

      // Sum field-estimated CY across all volumetric stops
      const totalEstimatedCY = stops
        .filter(s => (DEBRIS_TYPES[s.debrisType as DebrisType]?.measurementMode ?? 'volumetric') === 'volumetric')
        .reduce((sum, s) => sum + (parseFloat(s.estimatedCY || '0') || 0), 0);

      // Sum unit counts across unit-type stops
      const totalUnitCount = stops
        .filter(s => DEBRIS_TYPES[s.debrisType as DebrisType]?.measurementMode === 'unit')
        .reduce((sum, s) => sum + (parseInt(s.unitCount || '0') || 0), 0);

      // Create the draft ticket
      const ticket = await createLoadTicket({
        incidentId,
        ticketNumber: ticketNum,
        truckId: crew?.truckId || '',
        truckNumber: '',
        crewId: selectedCrew || '',
        contractorId: crew?.contractorId || '',
        zoneId: selectedZone || '',
        disposalSiteId: '',
        debrisType: dominantType,
        measurementMode: cfg?.measurementMode || 'volumetric',
        loadPercentage: '',
        calculatedCubicYards: totalEstimatedCY > 0 ? String(totalEstimatedCY) : '',
        unitCount: totalUnitCount > 0 ? String(totalUnitCount) : '',
        unitSubcategory: '',
        diameterRange: '',
        weight: '',
        weightUnit: '',
        scaleTicketNumber: '',
        pickupLat: centroidLat,
        pickupLng: centroidLng,
        pickupAddress: firstAddr,
        pickupTime: new Date().toISOString(),
        pickupStopsJson: stopsJson,
        disposalTime: '',
        monitorName: '',
        monitorSignature: '',
        driverSignature: '',
        verifiedAt: '',
        qrCode: ticketNum,
        status: 'draft' as any,
        photoBeforeUrl: '',
        photoAfterUrl: '',
        photoLoadUrl: '',
        notes: `Auto-generated from ${stops.length} field pickup stops`,
      });

      if (ticket) {
        // Generate QR code for the verification URL
        const verifyUrl = `${window.location.origin}/debris/verify/${ticket.id || ticket.Rid}`;
        try {
          const qrDataUrl = await QRCode.toDataURL(verifyUrl, { width: 300, margin: 1 });
          setGeneratedTicketQR(qrDataUrl);
          setGeneratedTicketNum(ticketNum);
        } catch { /* QR generation failed — not critical */ }

        notifications.show({
          title: 'Load Ticket Created',
          message: `Ticket ${ticketNum} generated from ${stops.length} stops. Show the QR code to the monitor at the disposal site.`,
          color: 'var(--color-success)',
          autoClose: 8000,
        });
        setStops([]);
      }
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Failed to generate ticket',
        color: 'var(--color-error)',
      });
    } finally {
      setSaving(false);
    }
  };

  const activeCrew = iCrews.find(c => String(c.id) === selectedCrew);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Field Pickup</Title>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          onClick={() => { loadZones(); loadCrews(); }}>Refresh</Button>
      </Group>

      <Text size="sm" c="dimmed">
        Build a load by adding pickup stops as your crew works. When the truck is full,
        tap "Truck is Full" to generate a load ticket with a QR code for the disposal site monitor.
      </Text>

      {/* QR code display after ticket generation */}
      {generatedTicketQR && (
        <Paper p="lg" radius="md" withBorder style={{ borderColor: '#16a34a', borderWidth: 2, textAlign: 'center' }}>
          <Text fw={700} size="lg" c="var(--color-success)" mb="sm">Load Ticket Generated: {generatedTicketNum}</Text>
          <img src={generatedTicketQR} alt="Ticket QR Code"
            style={{ width: 250, height: 250, margin: '0 auto', display: 'block' }} />
          <Text size="sm" c="dimmed" mt="sm">Show this QR to the monitor at the disposal site</Text>
          <Button mt="md" variant="light" onClick={() => { setGeneratedTicketQR(''); setGeneratedTicketNum(''); }}>
            Start Next Load
          </Button>
        </Paper>
      )}

      {/* Crew / Zone selection */}
      <Paper p="md" radius="md" withBorder>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Select label="Your Crew" data={crewSelect} value={selectedCrew}
            onChange={v => setSelectedCrew(v)} searchable
            placeholder="Select your crew..." size="md" />
          <Select label="Working Zone" data={zoneSelect} value={selectedZone}
            onChange={v => setSelectedZone(v)} searchable
            placeholder="Select zone..." size="md" />
        </SimpleGrid>
        {activeCrew && (
          <Group mt="sm" gap="md">
            <Badge>{activeCrew.crewType || 'crew'}</Badge>
            {activeCrew.driverName && <Text size="sm">Driver: <strong>{activeCrew.driverName}</strong></Text>}
            {activeCrew.truckId && (
              <Text size="sm">Truck: <strong>{trucks.find(t => String(t.id) === activeCrew.truckId)?.truckNumber || activeCrew.truckId}</strong></Text>
            )}
          </Group>
        )}
      </Paper>

      {/* Active pickup form */}
      {currentStop ? (
        <Paper p="lg" radius="md" withBorder style={{ borderColor: 'var(--color-primary-700)', borderWidth: 2 }}>
          <Group justify="space-between" mb="md">
            <Text fw={700} size="lg">New Pickup Stop</Text>
            <Badge size="lg" variant="light">{fmtDateTime(currentStop.timestamp)}</Badge>
          </Group>

          <Stack gap="md">
            {/* Address + GPS */}
            <Group align="flex-end" gap="sm">
              <div style={{ flex: 1, position: 'relative' }}>
                <TextInput label="Address / Location" placeholder="Start typing an address..."
                  value={currentStop.address}
                  onChange={e => handleAddrSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setShowAddrResults(false), 200)}
                  size="md" />
                {showAddrResults && addrResults.length > 0 && (
                  <Paper withBorder shadow="md" style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    maxHeight: 200, overflow: 'auto',
                  }}>
                    {addrResults.map((r, i) => (
                      <div key={i} style={{ padding: '8px 16px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f0f0f0' }}
                        onMouseDown={() => {
                          setCurrentStop(prev => prev ? {
                            ...prev,
                            address: r.display_name,
                            lat: r.lat as any,
                            lng: r.lon as any,
                          } : prev);
                          setShowAddrResults(false);
                        }}>
                        {r.display_name}
                      </div>
                    ))}
                  </Paper>
                )}
              </div>
              <Button size="md" variant="light" leftSection={<IconCurrentLocation size={16} />}
                loading={gettingLocation} onClick={getGPS}>
                GPS
              </Button>
            </Group>

            {currentStop.lat && (
              <Text size="xs" c="dimmed">
                <IconMapPin size={12} style={{ verticalAlign: 'middle' }} /> {currentStop.lat}, {currentStop.lng}
              </Text>
            )}

            <Select label="Debris Type" data={DEBRIS_TYPE_OPTIONS}
              value={currentStop.debrisType || null}
              onChange={v => setCurrentStop(prev => prev ? { ...prev, debrisType: v ?? '' } : prev)}
              searchable size="md" />

            {/* Per-stop measurement — adapts to debris type */}
            {(() => {
              const cfg = currentStop.debrisType ? DEBRIS_TYPES[currentStop.debrisType as DebrisType] : null;
              const mode = cfg?.measurementMode ?? 'volumetric';
              if (mode === 'volumetric') {
                return (
                  <TextInput
                    label="Estimated Cubic Yards (CY)"
                    description="Field estimate — tower monitor will confirm at disposal site"
                    type="number"
                    size="md"
                    value={currentStop.estimatedCY}
                    onChange={e => setCurrentStop(prev => prev ? { ...prev, estimatedCY: e.target.value } : prev)}
                    placeholder="e.g. 12"
                    min={0}
                  />
                );
              }
              return (
                <SimpleGrid cols={{ base: 1, sm: mode === 'unit' && cfg?.subcategories ? 3 : 2 }} spacing="sm">
                  {mode === 'unit' && (
                    <>
                      <TextInput label={`Quantity (${cfg?.unitLabel || 'units'})`} type="number" size="md"
                        value={currentStop.unitCount}
                        onChange={e => setCurrentStop(prev => prev ? { ...prev, unitCount: e.target.value } : prev)}
                        placeholder="How many?" />
                      {cfg?.subcategories && (
                        <Select label="Subcategory" size="md"
                          data={cfg.subcategories.map(s => ({ value: s, label: s }))}
                          value={currentStop.unitSubcategory || null}
                          onChange={v => setCurrentStop(prev => prev ? { ...prev, unitSubcategory: v ?? '' } : prev)} />
                      )}
                      {cfg?.hasDiameter && (
                        <Select label="Diameter Range" size="md"
                          data={['6-12"', '12-24"', '24-36"', '36-48"', '48"+'].map(d => ({ value: d, label: d }))}
                          value={currentStop.diameterRange || null}
                          onChange={v => setCurrentStop(prev => prev ? { ...prev, diameterRange: v ?? '' } : prev)} />
                      )}
                    </>
                  )}
                  {mode === 'weight' && (
                    <>
                      <TextInput label={`Weight (${cfg?.weightUnit || 'lbs'})`} type="number" size="md"
                        value={currentStop.weight}
                        onChange={e => setCurrentStop(prev => prev ? { ...prev, weight: e.target.value } : prev)} />
                      <Select label="Unit" size="md"
                        data={[{ value: 'lbs', label: 'Pounds' }, { value: 'tons', label: 'Tons' }]}
                        value={currentStop.weightUnit || 'lbs'}
                        onChange={v => setCurrentStop(prev => prev ? { ...prev, weightUnit: v ?? 'lbs' } : prev)} />
                    </>
                  )}
                </SimpleGrid>
              );
            })()}

            {/* Photo capture */}
            <Divider label="Photos" labelPosition="left" />

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="sm" fw={600} mb={4}>Before Photo</Text>
                {currentStop.beforePhotoData ? (
                  <div style={{ position: 'relative' }}>
                    <img src={currentStop.beforePhotoData} alt="Before"
                      style={{ width: '100%', borderRadius: 4, maxHeight: 200, objectFit: 'cover' }} />
                    <ActionIcon size="sm" color="var(--color-error)" variant="filled"
                      style={{ position: 'absolute', top: 4, right: 4 }}
                      onClick={() => setCurrentStop(prev => prev ? { ...prev, beforePhotoData: null, beforePhotoName: '' } : prev)}>
                      <IconTrash size={12} />
                    </ActionIcon>
                  </div>
                ) : (
                  <label style={{ cursor: 'pointer' }}>
                    <Paper p="lg" radius="md" withBorder
                      style={{ textAlign: 'center', background: '#F8F8F8', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                      <IconCamera size={32} color="#888" />
                      <Text size="xs" c="dimmed" mt={4}>Tap to capture</Text>
                    </Paper>
                    <input type="file" accept="image/*" capture="environment"
                      style={{ display: 'none' }}
                      onChange={e => handlePhoto('before', e.target.files?.[0] ?? null)} />
                  </label>
                )}
              </div>

              <div>
                <Text size="sm" fw={600} mb={4}>After Photo</Text>
                {currentStop.afterPhotoData ? (
                  <div style={{ position: 'relative' }}>
                    <img src={currentStop.afterPhotoData} alt="After"
                      style={{ width: '100%', borderRadius: 4, maxHeight: 200, objectFit: 'cover' }} />
                    <ActionIcon size="sm" color="var(--color-error)" variant="filled"
                      style={{ position: 'absolute', top: 4, right: 4 }}
                      onClick={() => setCurrentStop(prev => prev ? { ...prev, afterPhotoData: null, afterPhotoName: '' } : prev)}>
                      <IconTrash size={12} />
                    </ActionIcon>
                  </div>
                ) : (
                  <label style={{ cursor: 'pointer' }}>
                    <Paper p="lg" radius="md" withBorder
                      style={{ textAlign: 'center', background: '#F8F8F8', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                      <IconCamera size={32} color="#888" />
                      <Text size="xs" c="dimmed" mt={4}>Tap to capture</Text>
                    </Paper>
                    <input type="file" accept="image/*" capture="environment"
                      style={{ display: 'none' }}
                      onChange={e => handlePhoto('after', e.target.files?.[0] ?? null)} />
                  </label>
                )}
              </div>
            </SimpleGrid>

            <Textarea label="Notes" placeholder="Any observations..."
              value={currentStop.notes}
              onChange={e => setCurrentStop(prev => prev ? { ...prev, notes: e.target.value } : prev)}
              autosize minRows={2} size="md" />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCurrentStop(null)}>Cancel</Button>
              <Button size="md" leftSection={<IconCheck size={16} />} onClick={saveStop}>
                Add to Run
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : canEdit ? (
        <Button size="lg" fullWidth variant="light" leftSection={<IconPlus size={18} />}
          onClick={startNewStop} style={{ minHeight: 60 }}>
          {stops.length === 0 ? 'Add First Pickup Stop' : 'Add Next Stop'}
        </Button>
      ) : null}

      {/* Accumulated stops */}
      {stops.length > 0 && (
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <div>
              <Text fw={700} size="lg">Building a Load — {stops.length} stop{stops.length !== 1 ? 's' : ''}</Text>
              {/* Running tally of debris types */}
              {(() => {
                const tally: Record<string, { count: number; units: number; cy: number; weight: number }> = {};
                stops.forEach(s => {
                  if (!s.debrisType) return;
                  if (!tally[s.debrisType]) tally[s.debrisType] = { count: 0, units: 0, cy: 0, weight: 0 };
                  tally[s.debrisType].count++;
                  tally[s.debrisType].units += parseInt(s.unitCount || '0') || 0;
                  tally[s.debrisType].cy += parseFloat(s.estimatedCY || '0') || 0;
                  tally[s.debrisType].weight += parseFloat(s.weight || '0') || 0;
                });
                const parts = Object.entries(tally).map(([type, { count, units, cy, weight }]) => {
                  const cfg = DEBRIS_TYPES[type as DebrisType];
                  const label = cfg?.label || type;
                  if (cfg?.measurementMode === 'unit' && units > 0) return `${label}: ${units} ${cfg.unitLabel || 'units'}`;
                  if (cfg?.measurementMode === 'weight' && weight > 0) return `${label}: ${weight} ${cfg.weightUnit || 'lbs'}`;
                  if (cy > 0) return `${label}: ~${cy} CY`;
                  return `${label}: ${count} stop${count !== 1 ? 's' : ''}`;
                });
                if (parts.length === 0) return null;
                return <Text size="xs" c="dimmed">{parts.join(' · ')}</Text>;
              })()}
            </div>
            {canEdit && (
              <Button size="md" color="var(--color-success)" leftSection={<IconCheck size={16} />}
                loading={saving} onClick={completePickupAndGenerateTicket}
                style={{ minHeight: 44 }}>
                Truck is Full — Generate Ticket
              </Button>
            )}
          </Group>

          <Stack gap="xs">
            {stops.map((stop, i) => (
              <Paper key={stop.id} p="sm" radius="sm" withBorder>
                <Group justify="space-between">
                  <Group gap="sm">
                    <Badge size="lg" circle variant="light">{i + 1}</Badge>
                    <div>
                      <Text size="sm" fw={500}>{stop.address || `${stop.lat}, ${stop.lng}`}</Text>
                      <Group gap="xs">
                        {stop.debrisType && (
                          <Badge size="xs">{DEBRIS_TYPE_LABELS[stop.debrisType as DebrisType] ?? stop.debrisType}</Badge>
                        )}
                        <Text size="xs" c="dimmed">{fmtDateTime(stop.timestamp)}</Text>
                        {stop.beforePhotoData && <Badge size="xs" variant="light" color="var(--color-primary-700)" leftSection={<IconPhoto size={10} />}>Before</Badge>}
                        {stop.afterPhotoData && <Badge size="xs" variant="light" color="var(--color-success)" leftSection={<IconPhoto size={10} />}>After</Badge>}
                      </Group>
                      {stop.notes && <Text size="xs" c="dimmed" mt={2}>{stop.notes}</Text>}
                    </div>
                  </Group>
                  {canEdit && (
                  <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => removeStop(stop.id)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                  )}
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Session summary */}
      {stops.length > 0 && (
        <Paper p="md" radius="md" withBorder style={{ background: 'rgba(75,63,255,0.08)' }}>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Stops</Text>
              <Text size="xl" fw={700}>{stops.length}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Photos</Text>
              <Text size="xl" fw={700}>
                {stops.filter(s => s.beforePhotoData).length + stops.filter(s => s.afterPhotoData).length}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Zone</Text>
              <Text fw={600}>{iZones.find(z => String(z.id) === selectedZone)?.zoneName ?? 'Not set'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Crew</Text>
              <Text fw={600}>{activeCrew?.crewName ?? 'Not set'}</Text>
            </div>
          </SimpleGrid>
        </Paper>
      )}
    </Stack>
  );
}
